import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, MapPin, QrCode, FileText,
  Wallet, TrendingUp, UserPlus, Trash2, Plus, Loader2, Activity, Download
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell
} from "recharts";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "clients", label: "Clients", icon: Users },
  { id: "venues", label: "Venues", icon: MapPin },
  { id: "qr", label: "QR & Referral", icon: QrCode },
  { id: "statement", label: "Statement", icon: FileText },
];

const money = (n) => `£${Number(n || 0).toFixed(2)}`;

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-[#E7E5E4] rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-[#78716C]">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
      </div>
      <div className="mt-3 font-heading text-3xl text-navy">{value}</div>
    </div>
  );
}

function Overview({ earnings }) {
  if (!earnings) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div className="space-y-6" data-testid="nt-overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Wallet} label="Your earnings (80%)" value={money(earnings.nt_share)} accent="bg-teal/10 text-teal" />
        <Stat icon={TrendingUp} label="Gross revenue" value={money(earnings.total_gross)} accent="bg-navy/10 text-navy" />
        <Stat icon={UserPlus} label="QR sign-ups" value={earnings.signups} accent="bg-amber-100 text-amber-600" />
        <Stat icon={Activity} label="To MBBC (20%)" value={money(earnings.mbbc_share)} accent="bg-stone-100 text-stone-600" />
      </div>

      <div className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm">
        <h3 className="font-heading text-lg text-navy">Earnings — last 6 months</h3>
        <div className="mt-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={earnings.monthly} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E5E4" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#78716C" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#78716C" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4" }} />
              <Bar dataKey="earnings" radius={[6, 6, 0, 0]}>
                {earnings.monthly.map((m, i) => <Cell key={m.month ?? `cell-${i}`} fill="#2E9E94" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-[#E7E5E4] rounded-xl p-6 shadow-sm">
        <h3 className="font-heading text-lg text-navy">Recent QR-attributed sign-ups</h3>
        <div className="mt-4 divide-y divide-[#F5F5F4]">
          {earnings.recent.length === 0 && <p className="text-sm text-[#78716C] py-4">No sign-ups yet. Share your QR code to start earning.</p>}
          {earnings.recent.map((r) => (
            <div key={`${r.email}-${r.date}`} className="flex items-center justify-between py-3">
              <div>
                <div className="text-sm font-medium text-navy">{r.email}</div>
                <div className="text-xs text-[#78716C]">{r.plan} · {new Date(r.date).toLocaleDateString()}</div>
              </div>
              <div className="text-sm font-semibold text-teal">{money(r.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Clients() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", plan: "physical", notes: "" });
  const [open, setOpen] = useState(false);
  const [inbodyFor, setInbodyFor] = useState(null);
  const [inbody, setInbody] = useState({ weight: "", body_fat: "", muscle_mass: "", bmr: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/nt/clients"); setList(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/nt/clients", form); setForm({ name: "", email: "", phone: "", plan: "physical", notes: "" }); setOpen(false); load(); toast.success("Client added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/nt/clients/${id}`); load(); };
  const saveInbody = async (e) => {
    e.preventDefault();
    await api.post(`/nt/clients/${inbodyFor}/inbody`, {
      weight: +inbody.weight || 0, body_fat: +inbody.body_fat || 0, muscle_mass: +inbody.muscle_mass || 0, bmr: +inbody.bmr || 0,
    });
    setInbodyFor(null); setInbody({ weight: "", body_fat: "", muscle_mass: "", bmr: "" }); load(); toast.success("BIA check-in logged");
  };

  return (
    <div className="space-y-5" data-testid="nt-clients">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-xl text-navy">Your clients ({list.length})</h3>
        <button onClick={() => setOpen(!open)} data-testid="add-client-btn" className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-dark">
          <Plus className="w-4 h-4" /> Add client
        </button>
      </div>

      {open && (
        <form onSubmit={add} className="bg-white border border-[#E7E5E4] rounded-xl p-5 grid sm:grid-cols-2 gap-3">
          <input required placeholder="Name" data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm">
            <option value="physical">Physical</option><option value="virtual">Virtual</option><option value="hybrid">Hybrid</option>
          </select>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sm:col-span-2 border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <button type="submit" data-testid="save-client" className="bg-teal text-white rounded-lg px-4 py-2 text-sm font-medium">Save client</button>
        </form>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {list.map((c) => {
          const latest = c.inbody?.[c.inbody.length - 1];
          return (
            <div key={c.id} className="bg-white border border-[#E7E5E4] rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-navy">{c.name}</div>
                  <div className="text-xs text-[#78716C]">{c.email || "—"} · {c.phone || "—"}</div>
                  <span className="mt-2 inline-block bg-[#F5F5F4] text-navy text-xs px-2.5 py-0.5 rounded-full capitalize">{c.plan}</span>
                </div>
                <button onClick={() => del(c.id)} className="text-[#A8A29E] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
              {latest && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="bg-[#F5F5F4] rounded-lg py-2"><div className="text-xs text-[#78716C]">Weight</div><div className="font-heading text-navy">{latest.weight}kg</div></div>
                  <div className="bg-[#F5F5F4] rounded-lg py-2"><div className="text-xs text-[#78716C]">Body fat</div><div className="font-heading text-navy">{latest.body_fat}%</div></div>
                  <div className="bg-[#F5F5F4] rounded-lg py-2"><div className="text-xs text-[#78716C]">Muscle</div><div className="font-heading text-navy">{latest.muscle_mass}kg</div></div>
                </div>
              )}
              {inbodyFor === c.id ? (
                <form onSubmit={saveInbody} className="mt-3 grid grid-cols-2 gap-2">
                  <input placeholder="Weight kg" value={inbody.weight} onChange={(e) => setInbody({ ...inbody, weight: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-1.5 text-sm" />
                  <input placeholder="Body fat %" value={inbody.body_fat} onChange={(e) => setInbody({ ...inbody, body_fat: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-1.5 text-sm" />
                  <input placeholder="Muscle kg" value={inbody.muscle_mass} onChange={(e) => setInbody({ ...inbody, muscle_mass: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-1.5 text-sm" />
                  <input placeholder="BMR" value={inbody.bmr} onChange={(e) => setInbody({ ...inbody, bmr: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-1.5 text-sm" />
                  <button type="submit" className="col-span-2 bg-navy text-white rounded-lg py-1.5 text-sm">Save check-in</button>
                </form>
              ) : (
                <button onClick={() => setInbodyFor(c.id)} className="mt-3 text-sm text-teal font-medium inline-flex items-center gap-1">
                  <Activity className="w-3.5 h-3.5" /> Log BIA check-in
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Venues() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", address: "", city: "", postcode: "", schedule: "", capacity: 0 });
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get("/nt/venues"); setList(data); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/nt/venues", { ...form, capacity: +form.capacity || 0 });
    setForm({ name: "", address: "", city: "", postcode: "", schedule: "", capacity: 0 }); setOpen(false); load(); toast.success("Venue added");
  };
  const del = async (id) => { await api.delete(`/nt/venues/${id}`); load(); };

  return (
    <div className="space-y-5" data-testid="nt-venues">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-xl text-navy">Your venues ({list.length})</h3>
        <button onClick={() => setOpen(!open)} data-testid="add-venue-btn" className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-dark">
          <Plus className="w-4 h-4" /> Add venue
        </button>
      </div>
      {open && (
        <form onSubmit={add} className="bg-white border border-[#E7E5E4] rounded-xl p-5 grid sm:grid-cols-2 gap-3">
          <input required placeholder="Venue name" data-testid="venue-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Schedule (e.g. Mon 6:30pm)" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="border border-[#E7E5E4] rounded-lg px-3 py-2 text-sm" />
          <button type="submit" data-testid="save-venue" className="bg-teal text-white rounded-lg px-4 py-2 text-sm font-medium">Save venue</button>
        </form>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((v) => (
          <div key={v.id} className="bg-white border border-[#E7E5E4] rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-teal/10 text-teal flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
              <button onClick={() => del(v.id)} className="text-[#A8A29E] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-3 font-medium text-navy">{v.name}</div>
            <div className="text-xs text-[#78716C]">{[v.address, v.city, v.postcode].filter(Boolean).join(", ")}</div>
            <div className="mt-2 text-sm text-[#44403C]">{v.schedule}</div>
            {v.capacity > 0 && <div className="text-xs text-[#78716C] mt-1">Capacity: {v.capacity}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function QRView() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/nt/qr?origin=${encodeURIComponent(window.location.origin)}`).then((r) => setData(r.data));
  }, []);
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div className="max-w-md" data-testid="nt-qr">
      <div className="bg-white border border-[#E7E5E4] rounded-2xl p-8 text-center shadow-sm">
        <h3 className="font-heading text-xl text-navy">Your personal sign-up QR</h3>
        <p className="text-sm text-[#78716C] mt-1">Every sign-up scanned here is attributed to you.</p>
        <img src={data.qr_png} alt="QR" className="mx-auto mt-6 w-56 h-56" data-testid="qr-image" />
        <div className="mt-5 bg-[#F5F5F4] rounded-lg px-4 py-3">
          <div className="text-xs text-[#78716C]">Referral code</div>
          <div className="font-heading text-2xl text-navy tracking-wide">{data.referral_code}</div>
        </div>
        <button onClick={() => { navigator.clipboard.writeText(data.join_url); toast.success("Link copied!"); }}
          className="mt-4 w-full bg-navy text-white rounded-full py-3 text-sm font-medium hover:bg-navy-dark">Copy sign-up link</button>
        <p className="mt-3 text-xs text-[#A8A29E] break-all">{data.join_url}</p>
      </div>
    </div>
  );
}

function Statement() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/nt/statement").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const rows = [
    ["Gross revenue (QR-attributed)", money(data.gross_revenue), false],
    ["MBBC commission (20%)", `– ${money(data.mbbc_commission)}`, true],
    ["Your share (80%)", money(data.nt_share), false],
    ["Equipment lease (fixed)", `– ${money(data.inbody_lease)}`, true],
    ["Arrangement fee", `– ${money(data.arrangement_fee)}`, true],
  ];
  return (
    <div className="max-w-2xl" data-testid="nt-statement">
      <div className="bg-white border border-[#E7E5E4] rounded-2xl p-8 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-xl text-navy">Monthly Financial Statement</h3>
            <p className="text-sm text-[#78716C]">{data.month_label} · {data.signups} sign-ups</p>
          </div>
          <FileText className="w-8 h-8 text-teal" />
        </div>
        <div className="mt-6 divide-y divide-[#F5F5F4]">
          {rows.map(([l, v, neg]) => (
            <div key={l} className="flex items-center justify-between py-3 text-sm">
              <span className="text-[#57534E]">{l}</span>
              <span className={`font-medium ${neg ? "text-red-500" : "text-navy"}`}>{v}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 bg-navy text-white rounded-xl px-5 py-4 flex items-center justify-between">
          <span className="font-medium">Net payout</span>
          <span className="font-heading text-2xl" data-testid="net-payout">{money(data.net_payout)}</span>
        </div>
      </div>
    </div>
  );
}

export default function NTDashboard() {
  usePageTitle("Therapist Dashboard");
  const [active, setActive] = useState("overview");
  const [earnings, setEarnings] = useState(null);

  useEffect(() => { api.get("/nt/earnings").then((r) => setEarnings(r.data)).catch(() => {}); }, []);

  return (
    <DashboardLayout subtitle="Therapist" navItems={NAV} active={active} onNav={setActive}>
      {active === "overview" && <Overview earnings={earnings} />}
      {active === "clients" && <Clients />}
      {active === "venues" && <Venues />}
      {active === "qr" && <QRView />}
      {active === "statement" && <Statement />}
    </DashboardLayout>
  );
}
